import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, Building2, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BulkAssignSupplierModal({ open, onClose, suppliers = [], onComplete }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("ANY");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetSupplierId, setTargetSupplierId] = useState("");
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/products?limit=10000`);
      setProducts(Array.isArray(r.data) ? r.data : r.data.products || []);
    } catch (e) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch("");
      setUnassignedOnly(false);
      setSupplierFilter("ANY");
      setTargetSupplierId("");
      setOverwriteExisting(false);
      setConfirming(false);
      fetchProducts();
    }
  }, [open]);

  const supplierLookup = useMemo(() => {
    const map = {};
    for (const s of suppliers) {
      map[s.id] = s.name;
    }
    return map;
  }, [suppliers]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((p) => {
      if (unassignedOnly && (p.supplier_id || p.supplier)) return false;
      if (supplierFilter && supplierFilter !== "ANY") {
        if (supplierFilter === "UNASSIGNED") {
          if (p.supplier_id || p.supplier) return false;
        } else if (p.supplier_id !== supplierFilter) {
          return false;
        }
      }
      if (!term) return true;
      const blob = `${p.plu_code || ""} ${p.name || ""} ${p.barcode || ""}`.toLowerCase();
      return blob.includes(term);
    });
  }, [products, search, unassignedOnly, supplierFilter]);

  const toggleId = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAllVisible = (checked) => {
    const next = new Set(selectedIds);
    for (const p of filtered) {
      if (checked) next.add(p.id);
      else next.delete(p.id);
    }
    setSelectedIds(next);
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));

  // Summary of what will happen
  const summary = useMemo(() => {
    let unassigned = 0;
    const fromSupplier = {}; // supplier_id → count
    for (const p of products) {
      if (!selectedIds.has(p.id)) continue;
      const current = p.supplier_id || "";
      if (!current) unassigned += 1;
      else if (current === targetSupplierId) {
        // skip — already on target
      } else {
        fromSupplier[current] = (fromSupplier[current] || 0) + 1;
      }
    }
    const movesFromExisting = Object.values(fromSupplier).reduce((a, b) => a + b, 0);
    return {
      totalSelected: selectedIds.size,
      unassigned,
      movesFromExisting,
      fromBreakdown: Object.entries(fromSupplier)
        .map(([sid, count]) => ({ supplier_id: sid, name: supplierLookup[sid] || sid.slice(0, 8), count }))
        .sort((a, b) => b.count - a.count),
    };
  }, [products, selectedIds, targetSupplierId, supplierLookup]);

  const targetSupplierName = useMemo(() => {
    return suppliers.find((s) => s.id === targetSupplierId)?.name || "";
  }, [suppliers, targetSupplierId]);

  const handleStartConfirm = () => {
    if (!targetSupplierId) {
      toast.error("Please pick the target supplier");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Select at least one product");
      return;
    }
    setConfirming(true);
  };

  const handleSubmit = async () => {
    // Build product_ids list, optionally excluding products that already have a supplier when not overwriting
    let ids;
    if (overwriteExisting) {
      ids = Array.from(selectedIds);
    } else {
      ids = products.filter((p) => selectedIds.has(p.id) && !p.supplier_id).map((p) => p.id);
      if (ids.length === 0) {
        toast.error('All selected products already have a supplier. Tick "Overwrite existing assignments" to proceed.');
        return;
      }
    }

    setSaving(true);
    try {
      const r = await saveWithRetry(() => axios.post(`${API}/api/products/bulk-assign-supplier`, {
        supplier_id: targetSupplierId,
        product_ids: ids,
      }));
      toast.success(r.data?.message || `Assigned ${r.data?.products_updated} products`);
      setConfirming(false);
      setSelectedIds(new Set());
      if (onComplete) onComplete();
      onClose && onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk assign failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="bulk-supplier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Bulk Assign Products to a Supplier
          </DialogTitle>
          <DialogDescription>
            Tick the products you want to (re)assign, pick the target supplier below, and confirm.
            The backend will only modify each product's <code className="text-xs bg-gray-100 px-1 rounded">supplier_id</code>
            and <code className="text-xs bg-gray-100 px-1 rounded">supplier</code> — stock, prices, and GRV history stay untouched.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-200">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by PLU, name, barcode…"
              className="pl-9"
              data-testid="bulk-supplier-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Currently assigned to:</Label>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-[220px]" data-testid="bulk-supplier-filter-select">
                <SelectValue placeholder="Any supplier" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="ANY">Any supplier</SelectItem>
                <SelectItem value="UNASSIGNED">Unassigned only</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Product list */}
        <div className="flex-1 min-h-[260px] overflow-y-auto border border-gray-200 rounded-lg mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading products…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">No products match the current filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(c) => toggleAllVisible(!!c)}
                      data-testid="bulk-supplier-select-all"
                    />
                  </th>
                  <th className="text-left px-3 py-2">PLU</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Current Supplier</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((p) => {
                  const isSelected = selectedIds.has(p.id);
                  return (
                    <tr
                      key={p.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${isSelected ? "bg-indigo-50" : ""}`}
                      data-testid={`bulk-supplier-row-${p.id}`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleId(p.id)}
                          data-testid={`bulk-supplier-select-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.plu_code || "—"}</td>
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2 text-xs">
                        {p.supplier_id ? (
                          <span className="text-gray-700">
                            {supplierLookup[p.supplier_id] || p.supplier || p.supplier_id.slice(0, 8)}
                          </span>
                        ) : (
                          <span className="text-amber-600 italic">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length > 500 && (
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-xs italic text-gray-500 bg-amber-50">
                      Showing first 500 of {filtered.length}. Refine the filter to see more.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Selection summary + target */}
        <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 mt-3 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <strong>{selectedIds.size}</strong> product{selectedIds.size === 1 ? "" : "s"} selected
              {filtered.length > 0 && (
                <span className="text-gray-500 ml-1">(of {filtered.length} visible)</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
              data-testid="bulk-supplier-clear-selection"
            >
              Clear selection
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <Label className="text-sm whitespace-nowrap">Assign to supplier:</Label>
            <Select value={targetSupplierId} onValueChange={setTargetSupplierId}>
              <SelectTrigger className="w-[320px] bg-white" data-testid="bulk-supplier-target-select">
                <SelectValue placeholder="Pick a supplier…" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="overwriteExisting"
              checked={overwriteExisting}
              onCheckedChange={(c) => setOverwriteExisting(!!c)}
              data-testid="bulk-supplier-overwrite-toggle"
            />
            <Label htmlFor="overwriteExisting" className="font-normal cursor-pointer">
              Also overwrite products that <strong>already have a supplier</strong>
              <span className="text-gray-500">
                {" "}
                (otherwise only the <em>{summary.unassigned}</em> unassigned of your selection are touched)
              </span>
            </Label>
          </div>
        </div>

        {confirming && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4 mt-3" data-testid="bulk-supplier-confirm-summary">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-amber-900">
                  About to reassign {overwriteExisting ? summary.totalSelected : summary.unassigned} product(s) to{" "}
                  <span className="text-indigo-700">{targetSupplierName}</span>
                </p>
                <ul className="mt-2 space-y-1 text-amber-800">
                  <li>
                    <strong>{summary.unassigned}</strong> currently unassigned → will be linked.
                  </li>
                  {overwriteExisting ? (
                    <>
                      <li>
                        <strong>{summary.movesFromExisting}</strong> will be moved from another supplier:
                      </li>
                      <ul className="ml-5 list-disc text-xs">
                        {summary.fromBreakdown.map((b) => (
                          <li key={b.supplier_id}>
                            <strong>{b.count}</strong> from <em>{b.name}</em> → <em>{targetSupplierName}</em>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    summary.movesFromExisting > 0 && (
                      <li className="text-amber-700">
                        ⚠ <strong>{summary.movesFromExisting}</strong> selected products already have a supplier and will be <strong>skipped</strong> (tick "Overwrite" above to include them).
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 mt-3">
          <Button variant="outline" onClick={onClose} data-testid="bulk-supplier-cancel-btn">
            Cancel
          </Button>
          {!confirming && (
            <Button
              onClick={handleStartConfirm}
              disabled={!targetSupplierId || selectedIds.size === 0}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-supplier-review-btn"
            >
              Review &amp; Assign
            </Button>
          )}
          {confirming && (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-supplier-confirm-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning…
                </>
              ) : (
                <>
                  <Building2 className="h-4 w-4 mr-2" />
                  Confirm assign
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
